// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import layerControlHtml from 'text!html/layerControl.html';
import './addLayerControl';
import 'myWorld/base/control';
import 'myWorld/base/pluginButton';
import 'myWorld/uiComponents/confirmationDialog';
import 'jquery-ui';
import 'jquery-ui-touch';
import './layerControlRow';
import './layerControlGroup';
import 'jquery-contextMenu';

const tabHtml = $(layerControlHtml)
    .filter('#layers-tab-template')
    .html();

// Timer for long touch detection
let timerLongTouch;
// Long touch flag for preventing "normal touch event" trigger when long touch ends
let longTouch = false;

myw.LayersControl = myw.Control.extend({
    innerTemplate: _.template(tabHtml),
    events: {
        'click #layers_edit': 'enterEditMode',
        'click #layers_edit.active': 'exitEditMode',
        'click #layers_add': 'openAddLayersDialog',
        'click #state_save': 'save'
    },

    options: {
        v: 2 //options format version
    },

    /**
     * @class UI for users to control layers visibility
     * @param  {Application|Control}    owner    Owner of self
     * @constructs
     * @extends {Control}
     */
    initialize(owner, options) {
        //call control constructor
        myw.Control.prototype.initialize.call(this, owner, options);

        this.layerManager = this.app.map.layerManager;
        this.system = this.app.system;

        //everytime there is a change to the overlays on the map, update our list
        this.app.on('overlays-changed nativeAppMode-changed', this.render.bind(this));

        this.app.on('overlayState-changed', ev => {
            this.updateOverlayItem(ev.layer);
        });

        const layerList = options.layerList;

        //the call to layerManager will trigger an 'overlays-changed' event
        if (layerList) this.layerManager.setLayerList(layerList);
        else this.layerManager.ensureInitialLayers(); //only to get started as early as possible, since this is also called by GeoMapControl

        /**Tracks the mode the layer control is in.
         * @type {boolean} */
        this.isEditMode = false;

        this.render(); //In case the 'overlays-changed' event has already been triggered
    },

    /**
     * Called when application closes
     * @return {object} Current layer list state
     */
    getState() {
        return {
            layerList: this.layerManager.getState(),
            v: 2
        };
    },

    /**
     * Sets the layer list as defined in state
     * @param {object} state    As returned by getState
     */
    setState(state) {
        this.layerManager.setLayerList(state.layerList);
    },

    //upgrades options from pre 4.2 to the 4.2 format
    upgradeOptionsToV2(options) {
        //pre 4.2 layer list was saved as part of map state (which was undesirable if there was no layer control)
        const mapState = this.app.getInitialState('map');
        const mapLayerList = mapState && mapState.layerList;
        if (mapLayerList) options.layerList = mapLayerList;
        return options;
    },

    /**
     * Updates the overlay list to match the status of the layer manager
     */
    render() {
        this.$el.html(this.innerTemplate({ baseUrl: myw.baseUrl }));
        this.showLayerEditButtons();

        /** References the layerManager's layerList
         * It is maintained by any layer additions or deletions on the map
         * @type {array<layerListItem>} */
        this.layerList = this.layerManager.layerList;

        /** Holds the layers and layer groups views in the layerControl
         * @type {object<name, LayerControlRow|LayerControlGroup>} */
        this.rowViews = {};

        _.each(this.layerList, layerItem => {
            this.addLayerToPanel(layerItem);
        });

        this.translate(this.$el);
        this.delegateEvents();
        this.ensureMode();

        //ENH: remove layers not in layer manager's list (even though there is  )

        this._configureContextMenu();
    },

    /**
     * Uses jquery.contextMenu to create a menu that appears on right click on the layer list
     * The menu has options to 'restore to default', 'All on' and 'All off'
     * @private
     */
    _configureContextMenu() {
        const contextMenuTarget = '.layer-item-row, .layer-group-row';

        const self = this; // use closure as the context for the functions will be clicked ui element
        const isEditable = function (key, opt) {
            const layerId = this[0].dataset.layer;
            const item = _(self.layerList).findWhere({ layer_name: layerId });
            return item && item.layerDef.hasOwnProperty('owner');
        };
        const editPrivate = function (key, opt) {
            const layerId = this[0].dataset.layer;
            const item = _(self.layerList).findWhere({ layer_name: layerId });
            let options = {
                layerDef: item.layerDef,
                onChangeCallback: newDef => {
                    self.layerManager.removeLayerFomList(layerId);
                    if (newDef) {
                        self.layerManager.addLayerFromDef(newDef, true);
                    }
                }
            };
            new myw.DefineLayerControl(self.layerManager, options);
        };

        $.contextMenu({
            // define which elements trigger this menu
            selector: contextMenuTarget,
            zIndex: 2,
            // define the elements of the menu
            items: {
                all_on: {
                    name: this.msg('all_on'),
                    callback: this.toggleAllLayers.bind(this, true)
                },
                all_off: {
                    name: this.msg('all_off'),
                    callback: this.toggleAllLayers.bind(this, false)
                },
                restore: {
                    name: this.msg('restore_to_default'),
                    callback: this._restoreToDefault.bind(this)
                },
                edit: { name: this.msg('edit_private'), callback: editPrivate, visible: isEditable }
            }
        }).bind(this);
        if (myw.isTouchDevice && myw.Util.isIOS) {
            this.$(contextMenuTarget).on('touchstart touchmove touchend touchcancel', event => {
                //we pass the original event object because the jQuery event in ios
                //object is normalized to w3c specs and does not provide the TouchList
                //fogbugz(#9748)
                this.handleTouch(event, contextMenuTarget);
            });
        }
    },

    // this method is used for ios touch devices to handle touch events
    handleTouch(event, contextMenuTarget) {
        switch (event.type) {
            case 'touchstart':
                timerLongTouch = setTimeout(function () {
                    // Flag for preventing "normal touch event" trigger when touch ends.
                    longTouch = true;
                    this.$(contextMenuTarget).contextMenu({ x: event.pageX, y: event.pageY });
                }, 1000);
                break;
            case 'touchmove':
                clearTimeout(timerLongTouch);
                break;

            case 'touchend':
                // If timerLongTouch is still running, then this is not a long touch
                // so stop the timer
                clearTimeout(timerLongTouch);
                if (longTouch) {
                    event.preventDefault();
                    longTouch = false;
                }
                break;
            default:
                return;
        }
    },

    /**
     * Display dialog for user to confirm if he wants to reset layer list
     * Restores the layer list to the default user's layer list
     * If there is no default layer list, lists all available layers and groups
     * @private
     */
    _restoreToDefault() {
        myw.confirmationDialog({
            title: this.msg('restore_to_default'),
            msg: this.msg('restore_confirm_msg'),
            confirmCallback: this._restoreSavedLayerList.bind(this)
        });
    },

    /**
     * Sets up the saved layer list for the current application as the user's current layer list
     * If there isn't one, it will set up a list corresponding to the available layers
     * @private
     */
    async _restoreSavedLayerList() {
        const layerList = await this._getSavedLayerList();
        this.layerManager.setLayerList(layerList);
    },

    /**
     * Returns the saved layer list for the current application
     * If there isn't one, returns all available layers
     * @private
     */
    async _getSavedLayerList() {
        const state = await this.app.getSavedStateFor(this, true, true);
        if (state) return state.layerList;

        //check for pre 4.2 format
        const appState = await this.app.getSavedState(true);
        if (appState && appState.map && appState.map.layerList) {
            return appState.map.layerList;
        }

        //return all available layers
        return this.layerManager.getDefaultLayerList();
    },

    /**
     * Turns all the layers and layer groups ON or OFF depending on the 'show' flag
     * @param {boolean}   show   Whether all layers/groups should be turned on or off
     */
    toggleAllLayers(show) {
        _.each(this.rowViews, layerRow => {
            layerRow.checkCheckbox(show); //just checking doesnt make the layers visible
            layerRow.setLayerChecked();
        });
    },

    /**
     * Checks for user permissions
     * Shows the layer list edit, add and save buttons bar
     */
    showLayerEditButtons() {
        //Checks if the logged in user has permission to modify the layer list
        this.system.userHasPermission('modifyLayerList').then(hasPerm => {
            if (hasPerm) this.$('.navigation-bar').removeClass('hidden');
            this._setPanelHeight();

            this.system.userHasPermission('persistState').then(hasPerm => {
                if (!hasPerm) this.$('#state_save').remove();
            });
        });
    },

    /**
     * Adds a layer to the end of the layer panel list
     * @param {layerListItem|layerListGroup} layer          Layer/group to be added to the control
     */
    addLayerToPanel(layerItem) {
        if (layerItem.subLayers) {
            //its a group
            return this.addGroupToPanel(layerItem);
        } else {
            const layer = this.layerManager.getLayer(layerItem.layerDef.id);
            const newLayerRow = new myw.LayerControlRow({
                control: this,
                model: layerItem,
                layer: layer,
                description: this.layerManager.getLayerItemDescription(layerItem.layerDef)
            });
            this._cacheRows(layerItem, newLayerRow);

            // Add the new overlay button.
            this.$('#layers-overlays').append(newLayerRow.el);

            // Update the element based on the current basemap and zoom-level
            this.updateOverlayItem(layer);
        }
    },

    /**
     * Adds the group to the control
     * @param {layerListGroup} group
     */
    addGroupToPanel(group) {
        const newGroup = new myw.LayerControlGroup({
            control: this,
            model: group
        });

        // Add the new group button.
        this.$('#layers-overlays').append(newGroup.el);

        this.ensureMode();
        this._cacheRows(group, newGroup);
    },

    _cacheRows(layerItem, row) {
        const layer = this.layerManager.layers[layerItem.layer_name];

        if (layerItem.type !== 'layer_group' && !layer) return;

        const entryName = this._getListEntryName(layerItem);

        //Store the layer/layergroup view keyed by its name
        this.rowViews[entryName] = row;
    },

    /**
     * Returns an entry id for the layer control row cache
     * Appends "_group" for layer groups, so same named layers and layer groups can be differenciated
     * @param  {layerListItem|layerListGroup} layerItem
     * @return {string}
     * @private
     */
    _getListEntryName(layerItem) {
        const entryName = layerItem.layer_name;
        if (layerItem.type === 'layer_group') {
            return `${entryName}_group`;
        }
        return entryName;
    },

    /**
     * Called by the layout when the available area for self changes
     * Adjusts the height of the layer list so that scrolling works correctly
     */
    invalidateSize() {
        this._setPanelHeight();
    },

    /**
     * Sets the heights of the layers panel
     * This is required esp. to support all screen sizes and the use of virtual keyboard on touch devices
     * @private
     */
    _setPanelHeight() {
        const navBarHeight = this.$('.navigation-bar').hasClass('hidden')
            ? 0
            : this.$('.navigation-bar').outerHeight(),
            panelContentHeight = this.$el.height() - navBarHeight;

        this.$('#layers-overlays-container').height(panelContentHeight);
    },

    /**
     * Makes the layer rows sortable;
     * Disables the turn on and off capability
     * Shows the buttons required via the ensureMode method
     */
    enterEditMode() {
        this.app.recordFunctionalityAccess('core.layers_tab.organise');

        this._makeListSortable();

        this.isEditMode = true;
        this.ensureMode();

        _.each(this.rowViews, layer => {
            layer.disable();
        });
    },

    /**
     * Makes the list sortable
     */
    _makeListSortable() {
        const that = this;
        this.$('.sort-layers').sortable({
            cursor: 'move',
            handle: '.reorder-button',
            axis: 'y',
            start() {
                this.sortable_start = that
                    .$('.sort-layers')
                    .sortable('toArray', { attribute: 'data-layer' });
            },
            stop() {
                this.sortable_end = that
                    .$('.sort-layers')
                    .sortable('toArray', { attribute: 'data-layer' });
                that._sortLayersList(this.sortable_end);
            }
        });
    },

    /**
     * Removes sortability from the layer rows;
     * Enables the turn on and off capability of the layer
     * Hides the edit buttons via the ensureMode method
     */
    exitEditMode() {
        this.isEditMode = false;
        this.ensureMode();

        _.each(this.rowViews, layer => {
            layer.enable();
        });

        //only attempt to destroy the sortable  jquery widget if it has been initalized
        if (this.$('.sort-layers').hasClass('ui-sortable')) {
            this.$('.sort-layers').sortable('destroy');
        }
    },
    /**
     * Makes sure the layer buttons and form elements are hidden or shown according to the current mode
     */
    ensureMode() {
        this.$('.reorder-button, .layer-list-delete')[this.isEditMode ? 'show' : 'hide']();
        this.$(
            '.layer-checkbox-container, .layer-group-select, .master-layer-icon, .invalid-layer-icon'
        )[this.isEditMode ? 'hide' : 'show']();

        //Update edit button state and tooltip
        this.$('#layers_edit')
            .toggleClass('active', this.isEditMode)
            .prop('title', this.msg(this.isEditMode ? 'done_edit' : 'edit'));

        if (this.isEditMode) {
            //if the sortable jquery widget has not been initalized
            if (!this.$('.sort-layers').hasClass('ui-sortable')) {
                this._makeListSortable();
            }
        }
    },

    /**
     * handles the click on the add layer list button
     */
    openAddLayersDialog() {
        this.app.recordFunctionalityAccess('core.layers_tab.add_layers');
        if (!this.addLayerControl) {
            this.addLayerControl = new myw.AddLayerControl(this.layerManager);
        }
        this.addLayerControl.render();
    },

    /**
     * Sorts this.layerList to match the order specified in layerIdArray i.e. list of layer/group ids
     * @param  {Array<string>} layerIdArray Array of layer names in the desired layer order
     * @private
     */
    _sortLayersList(layerIdArray) {
        const layersbyName = {};

        _.each(this.layerList, layer => {
            const key =
                layer.type == 'layer_group' ? `group_${layer.layer_name}` : layer.layer_name;
            layersbyName[key] = layer;
        });

        this.layerList.length = 0; //empties the layerList so it can be repopulated in the correct order

        let sequence = 1;
        _.each(layerIdArray, layerName => {
            const l = layersbyName[layerName];
            l.sequence = sequence++;
            if (l.subLayers) {
                const subLayers = l.subLayers;
                l.subLayers = [];

                //update the sequence of all its subLayers
                _.each(subLayers, subLayer => {
                    subLayer.sequence = l.sequence;
                    l.subLayers.push(subLayer);
                });
            }
            this.layerList.push(l);
        });
    },

    /**
     * Updates a item in the layer list
     * Verifies isChecked and isEnabled status and changes the css styles acordingly
     * @param  {Layer} overlay
     */
    updateOverlayItem(overlay) {
        _.each(this.rowViews, layerView => {
            if (layerView.model.subLayers) {
                //Update any matching subLayer if the group is turned on
                const subLayerView = _.find(
                    layerView.subLayers,
                    subLayer => subLayer.model.layer_name === overlay.layerDef.id
                );

                if (subLayerView) {
                    const isGroupTurnedOn = this.layerManager.getLayerFromLayerList(
                        layerView.model.layer_name,
                        true
                    ).turned_on;
                    layerView.checkCheckbox(isGroupTurnedOn); //update the turned_on flag in group's view
                    subLayerView.update();
                }
            } else if (layerView.model.layer_name === overlay.layerDef.id) {
                //Update any matching layer
                layerView.update();
            }
        });
    },

    /**
     * Removes the layer from the model and asks the layerManager to remove it from the map
     * @param  {LayerControlRow} layer The layer view that is being removed
     */
    removeLayer(layer) {
        this.rowViews = _.without(this.rowViews, layer);
        this.layerManager.removeLayerFomList(layer.model.layer_name);
    },

    /**
     * Removes the group from the model and asks the layerManager to remove it
     * @param  {LayerControlGroup} group  The group view that is being removed
     */
    removeGroup(group) {
        // Remove the group
        this.rowViews = _.without(this.rowViews, group);
        this.layerManager.removeGroup(group.model.layer_name);
    },

    /**
     * update the layer list with any changes or create a new one if a new user
     */
    save() {
        this.app.recordFunctionalityAccess('core.layers_tab.save_state');
        //ENH: convert into button provided by application
        this.app
            .saveState(true)
            .then(
                () => this.app.message(this.msg('state_saved_ok'), 2000, 2000),
                () => this.app.message(this.msg('state_saved_not_ok'), 2000, 2000)
            );
        this.exitEditMode();
    },

    /**
     * Returns layer list information in a way suitable to send to the database
     * @private
     */
    _flattenLayerData() {
        let layerData = this.layerManager.layerList;

        _.each(layerData, layer => {
            if (layer.subLayers) {
                // its a group; remove the layers from its model
                layerData = _.without(layerData, layer);
                layerData.push(_.omit(layer, 'subLayers'));
                // Add the sublayers to the layer data
                const subLayers = layer.subLayers;
                layerData = _.union(layerData, subLayers);
            }
        });
        return _.map(layerData, l => _.omit(l, 'layerDef'));
    }
});

myw.LayersControl.prototype.buttons = {
    //used in phone layout
    view: myw.PluginButton.extend({
        id: 'a-layers',
        titleMsg: 'menu_msg',
        imgSrc: 'images/toolbar/layers.svg',
        action() {
            this.app.layout.pages.layers.toggle(true);
        }
    })
};

export default myw.LayersControl;
